import {
    createSlice,
    createAsyncThunk,
    createSelector,
    PayloadAction
} from "@reduxjs/toolkit";
// Deep imports rather than the barrel - see the note in index.tsx.
import {
    getRegimeMappings,
    getResolvedRegimeMappings
} from "@idwal/idwal-utilities/lib/regimeMappings";
import { InspectionsState } from "../types/inspections/InspectionsState";
import { RootState } from "../redux/store";
import {
    getInspectionsByOrgId,
    GetInspectionsRequestData
} from "../services/InspectionsServices";
import { DataSharingStatus } from "../constants/DataSharingConstants";
import {
    toDefectCodeChips,
    toRegimeDescriptions
} from "../utils/regimeMappings";

const initialState: InspectionsState = {
    Inspections: null,
    InspectionsStatus: "idle",
    selectedInspection: null,
    activeTab: 0,
    toggleDataSharingOptIn: undefined,
    dataSharingStatus: null,
    regimeMappings: undefined,
    regimeMappingsStatus: "idle"
};

export const inspectionsSlice = createSlice({
    name: "inspectionsSlice",
    initialState,
    reducers: {
        setSelectedInspection: (state, action: PayloadAction<string>) => {
            state.selectedInspection = action.payload;
        },
        setActiveTab: (state, action: PayloadAction<number>) => {
            state.activeTab = action.payload;
        },
        setToggleDataSharingOptIn: (
            state,
            action: PayloadAction<boolean | undefined>
        ) => {
            state.toggleDataSharingOptIn = action.payload;
        },
        setDataSharingStatus: (
            state,
            action: PayloadAction<DataSharingStatus | null>
        ) => {
            state.dataSharingStatus = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getInspectionsThunk.fulfilled, (state, action) => {
                state.Inspections = action.payload;
                state.InspectionsStatus = "succeeded";
            })
            .addCase(getInspectionsThunk.pending, (state, _action) => {
                state.InspectionsStatus = "pending";
            })
            .addCase(getInspectionsThunk.rejected, (state, _action) => {
                state.InspectionsStatus = "failed";
                state.Inspections = null;
            })
            .addCase(getRegimeMappingsThunk.pending, (state, _action) => {
                state.regimeMappingsStatus = "pending";
            })
            .addCase(getRegimeMappingsThunk.fulfilled, (state, action) => {
                state.regimeMappings = action.payload;
                state.regimeMappingsStatus = "succeeded";
            })
            .addCase(getRegimeMappingsThunk.rejected, (state, _action) => {
                // Elsewhere the codes are supplementary, so a failure leaves
                // the page working rather than surfacing an error. Fall back
                // to an empty set rather than undefined: the chips then still
                // show the Idwal defect code, which is what happened when a
                // code was missing from the bundled mappings. The status is
                // what tells this apart from a genuinely empty set, which a
                // Pre-PSC needs: it can't list its defects without the
                // mappings, so it shows the request error (see Defects).
                state.regimeMappings = {};
                state.regimeMappingsStatus = "failed";
            });
    }
});

export const getInspectionsThunk = createAsyncThunk(
    "inspections/getInspections",
    async (requestData: GetInspectionsRequestData) => {
        return getInspectionsByOrgId(requestData);
    }
);

export const getRegimeMappingsThunk = createAsyncThunk(
    "inspections/getRegimeMappings",
    async () => {
        try {
            // Joins the names on, which the tooltip needs.
            return await getResolvedRegimeMappings();
        } catch (error) {
            // The descriptions are a second request. Only the tooltip needs
            // them, so fall back to the codes alone rather than losing both.
            // Mappings are cached by the client, so this does not refetch.
            console.error("Failed to resolve regime descriptions.", error);
        }

        try {
            return await getRegimeMappings();
        } catch (error) {
            // The rejected case falls back to an empty set, which renders the
            // same as "no regime codes for this defect". Log it so a failing
            // fetch is distinguishable from a genuinely empty mapping.
            console.error("Failed to load regime mappings.", error);
            throw error;
        }
    }
);

export const inspectionsSelector = (state: RootState) => state.Inspections;
export const inspectionsActiveTabSelector = (state: RootState) =>
    state.Inspections.activeTab;
export const inspectionsToggleDataSharingOptInSelector = (state: RootState) =>
    state.Inspections.toggleDataSharingOptIn;
export const inspectionsDataSharingStatusSelector = (state: RootState) =>
    state.Inspections.dataSharingStatus;
export const regimeMappingsSelector = (state: RootState) =>
    state.Inspections.regimeMappings;
export const regimeMappingsStatusSelector = (state: RootState) =>
    state.Inspections.regimeMappingsStatus;

/**
 * The mappings flattened into the chip strings the shared component library
 * renders. Memoised so the sort runs once per change rather than per render,
 * and so consumers get a stable reference.
 */
export const defectCodeChipsSelector = createSelector(
    regimeMappingsSelector,
    toDefectCodeChips
);

/**
 * What each regime code is, for the chip tooltips. Memoised so the walk over
 * the mappings runs once per change rather than per render, and so consumers
 * get a stable reference.
 */
export const regimeDescriptionsSelector = createSelector(
    regimeMappingsSelector,
    toRegimeDescriptions
);

export const {
    setSelectedInspection,
    setActiveTab,
    setToggleDataSharingOptIn,
    setDataSharingStatus
} = inspectionsSlice.actions;

export default inspectionsSlice.reducer;
